import React, {
	useEffect,
	useState,
	useCallback,
	useRef,
	useMemo,
} from "react";
import { FaChevronRight } from "react-icons/fa";
import { RiKeyFill } from "react-icons/ri";
import useTrack, { eventTypes } from "../../../../hooks/useTrack";
import ProductCard from "../../ProductCard";
import BrandOverlay from "../BrandOverlay";
import { AnimatePresence } from "framer-motion";
import GET from "../../../../utils/GET";
import { UTM_PARAMS } from "../../../../conf";
import parseUtm from "../../../../utils/parseUtm";
import accessibilityProvider from "../../../../utils/accessibilityProvider";

const inModal = GET("modal");

const BrandRow = React.memo(
	({
		brand = {},
		order,
		shopifyShop,
		populateCart,
		modalVisible = false,
		upsell = false,
		postPurchase,
		onExpand,
		isLast,
		setPdpInfo,
		widgetState,
		idx,
		setLastIdx,
		lastIdx,
	}) => {
		const track = useTrack();
		const [viewDiscount, setViewDiscount] = useState(false);
		const brandRowRef = useRef(null);
		const brandRowOfferRef = useRef(null);

		const brandRowOfferCloseRef = useRef(null);

		const isPremium = useMemo(
			() => !!(brand && brand.premium_credit_used),
			[brand]
		);

		const utmType = useMemo(() => {
			if (!brand) return "";
			return upsell
				? "upsell"
				: brand.premium_credit_used
				? "premium"
				: "earned";
		}, [brand, upsell]);

		const earnedCreditUsed = useMemo(
			() => !!(brand && brand.earned_credit_used),
			[brand]
		);
		const emailCreditUsed = useMemo(
			() => !!(brand && brand.email_credit_used),
			[brand]
		);

		const eventMetadata = useMemo(
			() => (brand && brand.metadata) || {},
			[brand]
		);

		// for event tracking
		const [trigger, setTrigger] = useState(false);
		const [fired, setFired] = useState(false);

		const utmFlags = useMemo(() => {
			if (!brand.utm || !utmType) {
				return {};
			}

			return brand.utm
				.filter((flag) => flag.param_type === utmType)
				.reduce((flags, flag) => {
					return {
						...flags,
						[UTM_PARAMS[flag.param_name - 1]]: flag.param_value,
					};
				}, {});
		}, [brand, utmType]);

		// trigger only when the brand offset is in screen
		useEffect(() => {
			const {
				name,
				id,
				earned_credits,
				premium_credits,
				ml_order,
				preferred_display,
				preferred_start_date,
			} = brand;

			if (!modalVisible && trigger && !fired) {
				// console.log("FIRING EVENT FOR ", brand.name, " ===> ", isLast);
				if (
					isLast &&
					typeof onExpand === "function" &&
					(postPurchase || inModal) &&
					widgetState === "max"
				) {
					onExpand();
				}
				// if (idx <= lastIdx) {
				// 	return;
				// }
				// if (idx !== undefined && typeof setLastIdx === "function") {
				// 	setLastIdx(idx);
				// }
				// console.log("TRACKING: ", brand.name);
				track(eventTypes.WIDGET_BRAND_DISPLAY, {
					brand: name,
					brand_id: id,
					brand_view_order: order,
					premium_credit_used: isPremium,
					earned_credit_used: earnedCreditUsed,
					email_credit_used: emailCreditUsed,
					earned_credits,
					ml_order,
					premium_credits,
					preferred_display,
					preferred_start_date,
					...eventMetadata,
				});
				setFired(true);
			}
		}, [
			brand,
			track,
			order,
			modalVisible,
			trigger,
			postPurchase,
			fired,
			isPremium,
			earnedCreditUsed,
			emailCreditUsed,
			onExpand,
			isLast,
			setLastIdx,
			idx,
			lastIdx,
			widgetState,
			eventMetadata,
		]);

		const handleClick = () => {
			let {
				name,
				id,
				masked_url: url,
				ml_order,
				preferred_display,
				preferred_start_date,
			} = brand;
			track(eventTypes.CLICK_BRAND, {
				brand: name,
				brand_id: id,
				ml_order,
				brand_link: url,
				premium_credit_used: isPremium,
				earned_credit_used: earnedCreditUsed,
				email_credit_used: emailCreditUsed,
				preferred_display,
				preferred_start_date,
				...eventMetadata,
			});
			const brandWindow = window.open();
			url = parseUtm(url, utmFlags, brand.override_utm);
			if (!brandWindow) {
				window.location.href = url;
				return;
			}
			brandWindow.opener = null;
			brandWindow.location = url;
		};

		const triggerEvents = useCallback(
			(msgData) => {
				if (!brandRowRef.current) {
					return;
				}
				const scrollPos = msgData.value;
				const brandRowOffset =
					msgData.iframe +
					brandRowRef.current.getBoundingClientRect().y;

				if (scrollPos > brandRowOffset + 50 && !fired) {
					setTrigger(true);
				}
			},
			[fired]
		);

		const handleScroll = useCallback(() => {
			const msgData = {
				value: document.documentElement.scrollTop + window.innerHeight,
				iframe: 0,
			};

			triggerEvents(msgData);
		}, [triggerEvents]);

		useEffect(() => {
			if (!inModal) {
				return;
			}
			window.addEventListener("scroll", handleScroll);
			return () => window.removeEventListener("scroll", handleScroll);
		}, [handleScroll]);

		const handleTrackMessage = useCallback(
			({ data: msgData }) => {
				if (
					msgData.type !== "scroll" ||
					(!postPurchase && msgData.iframe === 0)
				) {
					return;
				}

				triggerEvents(msgData);
			},
			[postPurchase, triggerEvents]
		);

		useEffect(() => {
			window.addEventListener("message", handleTrackMessage);
		}, [handleTrackMessage]);

		const handleDiscountClick = useCallback(
			(val) => {
				if (val) {
					const {
						name,
						id,
						preferred_display,
						preferred_start_date,
					} = brand;
					track(eventTypes.WIDGET_OFFER_CLICK, {
						brand: name,
						brand_id: id,
						premium_credit_used: isPremium,
						earned_credit_used: earnedCreditUsed,
						email_credit_used: emailCreditUsed,
						preferred_display,
						preferred_start_date,
						...eventMetadata,
					});
					setTimeout(() => {
						if (
							brandRowOfferCloseRef &&
							brandRowOfferCloseRef.current
						) {
							brandRowOfferCloseRef.current.focus();
						}
					}, 0);
				} else {
					if (brandRowOfferRef && brandRowOfferRef.current) {
						brandRowOfferRef.current.focus();
					}
				}
				setViewDiscount(val);
			},
			[
				setViewDiscount,
				brand,
				track,
				isPremium,
				earnedCreditUsed,
				emailCreditUsed,
				eventMetadata,
			]
		);

		return (
			<section className="widget-brand-container" ref={brandRowRef}>
				<div className="widget-brand-header">
					<img
						src={brand.logo_url}
						alt=""
						className="widget-brand-header-image"
						onClick={handleClick}
						{...accessibilityProvider({
							name: `${brand.name} image`,
						})}
					/>
					<div
						className="widget-brand-header-title"
						onClick={handleClick}
						{...accessibilityProvider({
							name: `About ${brand.name}`,
						})}
					>
						<div className="widget-brand-header-title-name">
							{brand.name}
						</div>
						{brand.single_liner && brand.single_liner !== "" && (
							<div className="widget-brand-header-title-desc">
								{brand.single_liner}
							</div>
						)}
					</div>
					{brand.code && (
						<div
							className="widget-brand-header-discount widget-brand-header-btn"
							onClick={() => {
								handleDiscountClick(true);
							}}
							ref={brandRowOfferRef}
							{...accessibilityProvider({
								name: `Offer by ${brand.name}`,
							})}
						>
							Offer
							<RiKeyFill
								style={{
									marginLeft: "5px",
									fontSize: "1.15rem",
									animation: "jiggle 2.8s infinite",
								}}
							/>
						</div>
					)}
					{brand.masked_url && (
						<div className="widget-brand-header-cta widget-brand-header-btn">
							<span
								className="widget-brand-header-cta-text"
								onClick={handleClick}
								{...accessibilityProvider({
									name: `Visit ${brand.name}`,
								})}
							>
								Visit Shop
							</span>
							<FaChevronRight />
						</div>
					)}
				</div>
				<div className="widget-brand-separator" />
				{brand.name === "Rockets of Awesome" &&
					console.log(`brand`, brand)}
				<div className="widget-brand-body">
					{brand.products &&
						brand.products.map((product, id) => (
							<ProductCard
								key={id}
								overrideUtm={brand.override_utm}
								isPremium={isPremium}
								utmFlags={utmFlags}
								product={{ ...product, brand: brand.name }}
								populateCart={populateCart}
								shopifyShop={shopifyShop}
								order={id}
								modalVisible={modalVisible}
								ml_order={brand.ml_order}
								upsell={upsell}
								postPurchase={postPurchase}
								setPdpInfo={setPdpInfo}
								brandId={brand.id}
								earnedCreditUsed={earnedCreditUsed}
								emailCreditUsed={emailCreditUsed}
								brandMetadata={eventMetadata}
								preferredDisplay={brand.preferred_display}
								preferredStartDate={brand.preferred_start_date}
							/>
						))}
					<div className="widget-brand-body-spacer" />
				</div>
				<AnimatePresence>
					{viewDiscount && (
						<BrandOverlay
							brandName={brand.name}
							discount={brand.code}
							handleClose={handleDiscountClick}
							ref={brandRowOfferCloseRef}
						/>
					)}
				</AnimatePresence>
			</section>
		);
	}
);

export default BrandRow;
